import type { Names, Round } from '../../types';

export const possibleBids = [
  'Blind Nil',
  'Nil',
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  '10',
  '11',
  '12',
  '13',
] as const;

export const possibleMade = [
  '0',
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  '10',
  '11',
  '12',
  '13',
] as const;

/** Longest player or team name; every layout is designed to fit this many characters. */
export const MAX_NAME_LENGTH = 15;
export const NAME_LIMIT_MESSAGE = `${MAX_NAME_LENGTH} characters max`;

/** User-facing words. Internal discriminants below stay stable if these change. */
export const BID_LABEL = 'Bid';
export const MADE_LABEL = 'Made';

export const INPUT_TYPE_BID = 'Bid' as const;
export const INPUT_TYPE_MADE = 'Made' as const;

export type ScoringInputType =
  | typeof INPUT_TYPE_BID
  | typeof INPUT_TYPE_MADE;

export function labelForScoringInput(type: ScoringInputType): string {
  return type === INPUT_TYPE_BID ? BID_LABEL : MADE_LABEL;
}

/** Bids section heading. Plural of {@link BID_LABEL}. */
export const BID_SECTION_TITLE = `${BID_LABEL}s`;

/** Table board corner labels (Emerald board 474:18, Team made 1195:7978). */
export const TEAM_BID_LABEL = `Team ${BID_LABEL.toLowerCase()}`;
export const TEAM_MADE_LABEL = `Team ${MADE_LABEL.toLowerCase()}`;

/**
 * Nil-aware Team made sheet (`Emerald/Team made sheet`, decided 2026-10-21).
 * Never "kim's made" (made isn't a noun), and never "set" or "made it" for a
 * nil: a nil is broken by taking a trick.
 */
export const teamMadeSheetTitle = (teamName: string) =>
  `What ${teamName} ${MADE_LABEL.toLowerCase()}`;
/** "kim made", or "kim + Michael made" over the team total. */
export const playersMadeLabel = (names: string[]) =>
  `${names.join(' + ')} ${MADE_LABEL.toLowerCase()}`;
/** A nil bidder's keys stop here until "Show 4–13". */
export const NIL_KEYS_SHOWN = 4;
export const NIL_SHOW_MORE_LABEL = `Show ${NIL_KEYS_SHOWN}–13`;
/** Helps team bid only needs to know whether the nil held. */
export const NIL_HELD_LABEL = '0 tricks';
export const NIL_BROKEN_LABEL = '1 or more';

/** Under the board when a seat's made was split from a team entry (C25). */
export const AUTO_GENERATED_LEGEND =
  '* Auto-generated · edit to override';

/** The board's bid pill text (Emerald/Seat "Bid"): numbers stay as they are. */
export function bidPillLabel(bid: string | number): string {
  if (bid === 'Blind Nil') return 'B·NIL';
  if (bid === 'Nil') return 'NIL';
  return String(bid);
}

export const initialNames: Names = {
  t1p1Name: '',
  t1p2Name: '',
  t2p1Name: '',
  t2p2Name: '',
  team1Name: 'Team 1',
  team2Name: 'Team 2',
};

export const EMPTY_ROUND: Round = {
  team1BidsAndMade: {
    p1Bid: '',
    p2Bid: '',
    p1Made: '',
    p2Made: '',
  },
  team2BidsAndMade: {
    p1Bid: '',
    p2Bid: '',
    p1Made: '',
    p2Made: '',
  },
  dealerOverride: null,
  autoGeneratedMade: {
    team1P1: false,
    team1P2: false,
    team2P1: false,
    team2P2: false,
  },
};

export const BLIND_NIL = 'Blind Nil';
export const NIL = 'Nil';
export const TEAM1 = 'team1BidsAndMade';
export const TEAM2 = 'team2BidsAndMade';
export const TAKES_BAGS = 'takesBags';
export const HELPS_TEAM_BID = 'helpsTeamBid';
export const NO_BAGS_NO_HELP = 'noBagsNoHelp';

export const initialFirstDealerOrder = [
  `${TEAM1}.p1Bid`,
  `${TEAM2}.p1Bid`,
  `${TEAM1}.p2Bid`,
  `${TEAM2}.p2Bid`,
];
